import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { GRADE_SCALE_PRESETS } from '@repo/lib/constants/grade-scale-constants';
import { gradeScaleFormSchema, type GradeScaleFormSchema } from '@repo/schemas/grade-scale/gradeScale';
import { useCallback, useState } from 'react';
import { useFieldArray, useForm, useWatch, type UseFormReturn } from 'react-hook-form';

import type { GradeBand, GradeScheme } from './gradeScale.types';

/**
 * Splits the band at `splitIndex` into [min, boundary] and [boundary + 1, max].
 * The boundary is clamped so neither half collapses below the band edges (the
 * first band must not become [0, 0]); returns the bands unchanged when the
 * band is too narrow to split.
 */
function splitBand(bands: GradeBand[], splitIndex: number, desiredBoundary: number): GradeBand[] {
  const target: GradeBand | undefined = bands[splitIndex];
  if (target === undefined) {
    return bands;
  }
  const lowestBoundary: number = Math.max(target.minPercent, 1);
  const highestBoundary: number = target.maxPercent - 1;
  if (lowestBoundary > highestBoundary) {
    return bands;
  }
  const boundary: number = Math.min(Math.max(desiredBoundary, lowestBoundary), highestBoundary);
  return [...bands.slice(0, splitIndex), { ...target, maxPercent: boundary }, { name: '', minPercent: boundary + 1, maxPercent: target.maxPercent }, ...bands.slice(splitIndex + 1)];
}

export interface GradeScaleEditor {
  form: UseFormReturn<GradeScaleFormSchema>;
  schemes: GradeScheme[];
  selectedIndex: number;
  selectedScheme: GradeScheme | undefined;
  selectScheme: (index: number) => void;
  changeBandMin: (index: number, value: number | undefined) => void;
  changeBandMax: (index: number, value: number | undefined) => void;
  addBandAt: (percent: number) => void;
}

export function useGradeScaleEditor(initialSchemes?: GradeScheme[], initialSelectedIndex = 0): GradeScaleEditor {
  const form = useForm<GradeScaleFormSchema>({
    mode: 'onBlur',
    resolver: standardSchemaResolver(gradeScaleFormSchema),
    defaultValues: { schemes: initialSchemes ?? GRADE_SCALE_PRESETS },
  });
  const [selectedIndex, setSelectedIndex] = useState<number>(initialSelectedIndex);

  // Register `schemes` as a field array so `useWatch('schemes')` returns whole
  // scheme objects (incl. `bands`); without it RHF rebuilds the array from only
  // the rendered leaf fields and drops `bands` for non-selected schemes.
  useFieldArray({ control: form.control, name: 'schemes' });

  const schemes: GradeScheme[] = useWatch({
    control: form.control,
    name: 'schemes',
  });
  const selectedScheme: GradeScheme | undefined = schemes[selectedIndex];

  const updateSelectedBands = useCallback(
    (updater: (bands: GradeBand[]) => GradeBand[]) => {
      const bands: GradeBand[] = form.getValues(`schemes.${selectedIndex}.bands`);
      form.setValue(`schemes.${selectedIndex}.bands`, updater(bands), {
        shouldDirty: true,
        shouldValidate: true,
      });
    },
    [form, selectedIndex]
  );

  const changeBandMin = useCallback(
    (index: number, value: number | undefined) => {
      if (value === undefined || index === 0) {
        return;
      }
      form.setValue(`schemes.${selectedIndex}.bands.${index}.minPercent`, value, {
        shouldDirty: true,
      });
      form.setValue(`schemes.${selectedIndex}.bands.${index - 1}.maxPercent`, value - 1, { shouldDirty: true });
      // Validate only after both sides of the boundary are updated; validating
      // per setValue would flag the half-updated pair as discontinuous.
      void form.trigger([`schemes.${selectedIndex}.bands.${index}.minPercent`, `schemes.${selectedIndex}.bands.${index - 1}.maxPercent`]);
    },
    [form, selectedIndex]
  );

  const changeBandMax = useCallback(
    (index: number, value: number | undefined) => {
      const bandCount: number = form.getValues(`schemes.${selectedIndex}.bands`).length;
      if (value === undefined || index === bandCount - 1) {
        return;
      }
      form.setValue(`schemes.${selectedIndex}.bands.${index}.maxPercent`, value, {
        shouldDirty: true,
      });
      form.setValue(`schemes.${selectedIndex}.bands.${index + 1}.minPercent`, value + 1, { shouldDirty: true });
      void form.trigger([`schemes.${selectedIndex}.bands.${index}.maxPercent`, `schemes.${selectedIndex}.bands.${index + 1}.minPercent`]);
    },
    [form, selectedIndex]
  );

  const addBandAt = useCallback(
    (percent: number) => {
      updateSelectedBands((bands) => {
        const clamped: number = Math.min(Math.max(Math.round(percent), 0), 99);
        const splitIndex: number = bands.findIndex((band) => clamped >= band.minPercent && clamped <= band.maxPercent);
        if (splitIndex === -1) {
          return bands;
        }
        return splitBand(bands, splitIndex, clamped);
      });
    },
    [updateSelectedBands]
  );

  return {
    form,
    schemes,
    selectedIndex,
    selectedScheme,
    selectScheme: setSelectedIndex,
    changeBandMin,
    changeBandMax,
    addBandAt,
  };
}
